import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { checkControllerAppUpdate } from "./lib/appUpdateManager";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import SwitchLogin from "./pages/SwitchLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminDealers from "./pages/admin/AdminDealers";
import AdminTemplates from "./pages/admin/AdminTemplates";
import AdminClients from "./pages/admin/AdminClients";
import AdminPlans from "./pages/admin/AdminPlans";
import AdminDevices from "./pages/admin/AdminDevices";
import AdminAppUpdates from "./pages/admin/AdminAppUpdates";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminSalonOtpBilling from "./pages/admin/AdminSalonOtpBilling";
import AdminSeo from "./pages/admin/AdminSeo";
import AdminWebsite from "./pages/admin/AdminWebsite";
import AdminWebsiteAnalytics from "./pages/admin/AdminWebsiteAnalytics";
import AdminWebsiteEnquiries from "./pages/admin/AdminWebsiteEnquiries";
import AdminMarketingCRM from "./pages/admin/AdminMarketingCRM";
import WebsiteAnalyticsTracker from "./components/WebsiteAnalyticsTracker";
import DealerDashboard from "./pages/dealer/DealerDashboard";
import DealerClients from "./pages/dealer/DealerClients";
import DealerPlans from "./pages/dealer/DealerPlans";
import DealerTemplates from "./pages/dealer/DealerTemplates";
import DealerScreens from "./pages/dealer/DealerScreens";
import DealerPayments from "./pages/dealer/DealerPayments";
import ClientDashboard from "./pages/client/ClientDashboard";
import ClientDevices from "./pages/client/ClientDevices";
import ClientTemplates from "./pages/client/ClientTemplates";
import ClientStorefront from "./pages/client/ClientStorefront";
import HousingSocietyPanel from "./pages/client/HousingSocietyPanel";
import AccountingLayout from "./pages/client/society/accounting/AccountingLayout";
import SalonAccounting from "./pages/client/salon/SalonAccounting";
import ClientMedia from "./pages/client/ClientMedia";
import ClientPlaylists from "./pages/client/ClientPlaylists";
import ClientSchedules from "./pages/client/ClientSchedules";
import ClientPayments from "./pages/client/ClientPayments";
import PublicBooking from "./pages/PublicBooking";
import PublicAdAgency from "./pages/PublicAdAgency";
import PublicSocietyApproval from "./pages/PublicSocietyApproval";
import PublicSocietyBillPayment from "./pages/PublicSocietyBillPayment";
import SocietyGuardApp from "./pages/society/SocietyGuardApp";
import SocietyAuditorDashboard from "./pages/society/SocietyAuditorDashboard";
import SocietyMobileApp from "./pages/society/SocietyMobileApp";
import PublicStore from "./pages/store/PublicStore";
import HotelStaffApp from "./pages/hotel/HotelStaffApp";
import DiningSessionPage from "./pages/restaurant/DiningSessionPage";
import PublicRestaurantMenu from "./pages/restaurant/PublicRestaurantMenu";
import CustomDomainEntry from "./pages/CustomDomainEntry";
import { ECatalogueDetailPage, ECataloguePage } from "./pages/ECatalogue";
import AdminECatalogue from "./pages/admin/AdminECatalogue";
import {
  AboutPage,
  BlogDetailPage,
  BlogPage,
  ContactPage,
  DealerProgramPage,
  DownloadsPage,
  EnterpriseHome,
  FaqPage,
  IndustriesPage,
  IndustryDetailPage,
  NotFoundPage,
  PricingPage,
  ProductDetailPage,
  ProductsPage,
  RequestDemoPage,
  SimpleWebsitePage,
} from "./pages/EnterpriseWebsite";
// Use the new SignagePlayer implementation in its own folder (includes pairing UI)
import SignagePlayer from "./pages/SignagePlayer/SignagePlayer";

function isNativePlayerShell() {
 if (typeof window === "undefined") return false;
  if (process.env.REACT_APP_NATIVE_SHELL !== "player") return false;
  try {
    if (window.Capacitor && typeof window.Capacitor.getPlatform === "function") {
      return window.Capacitor.getPlatform() !== "web";
    }
  } catch {
    return false;
  }
  return false;
}

function isNativeControlShell() {
 if (typeof window === "undefined") return false;
  if (process.env.REACT_APP_NATIVE_SHELL !== "control") return false;
  try {
    if (window.Capacitor && typeof window.Capacitor.getPlatform === "function") {
      return window.Capacitor.getPlatform() !== "web";
    }
  } catch {
    return false;
  }
  return false;
}

function isNativeSocietyShell() {
  return process.env.REACT_APP_NATIVE_SHELL === "society";
}

function isNativeMarketingShell() {
  return process.env.REACT_APP_NATIVE_SHELL === "marketing";
}

function ProtectedRoute({ role, children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-sm text-[#6B7280] font-mono uppercase tracking-widest animate-pulse">Loading...</div>
      </div>
    );
  }
  if (user === false) return <ProtectedLoginRedirect />;
  const allowedRoles = Array.isArray(role) ? role : role ? [role] : [];
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    const home = user.role === "admin" ? "/admin" : user.role === "dealer" ? "/dealer" : user.role === "auditor" ? "/society/audit" : user.role === "society_staff" ? (user.societyRole === "security" ? "/society/guard" : "/society/mobile") : user.role === "society_member" ? "/society/mobile" : "/client";
    return <Navigate to={home} replace />;
  }
  return children;
}

function ProtectedLoginRedirect() {
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}${location.hash}`;
  // Keep the requested route in the URL as well as React navigation state.
  // Navigation state is intentionally ephemeral: a browser refresh on the
  // login screen clears it, which previously meant a person who refreshed
  // while re-authenticating was sent to their dashboard after login rather
  // than back to the page they had opened. Query state survives refreshes,
  // bookmarks and a restarted browser tab.
  const loginPath = `/login?${new URLSearchParams({ returnTo }).toString()}`;
  return <Navigate to={loginPath} replace state={{ returnTo }} />;
}

function SocietyStaffPanelRoute() {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-sm text-[#6B7280] font-mono uppercase tracking-widest animate-pulse">Loading...</div>
      </div>
    );
  }
  if (!user || user === false) return <ProtectedLoginRedirect />;
  if (user.role === "society_staff" && user.societyRole === "security") return <Navigate to="/society/guard" replace />;
  return (
    <SocietyClientRoute>
      <HousingSocietyPanel />
    </SocietyClientRoute>
  );
}

function SocietyClientRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-sm text-[#6B7280] font-mono uppercase tracking-widest animate-pulse">Loading...</div>
      </div>
    );
  }
  if (!user || user === false) return <ProtectedLoginRedirect />;
  if (String(user.vertical || "").toLowerCase() !== "society") {
    return <Navigate to="/client/storefront" replace />;
  }
  return children;
}

function SocietyWebCallDisabled() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F8FAFC] px-4 text-[#0F172A]">
      <div className="w-full max-w-md rounded-sm border border-[#E5E7EB] bg-white p-6 text-center shadow-sm">
        <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#64748B]">RP Society</div>
        <h1 className="mt-3 text-2xl font-black">Use the mobile app for calls</h1>
        <p className="mt-3 text-sm leading-6 text-[#475569]">
          Browser and WebView calling is disabled. Society video calls now work only through the native RP Society Android app.
        </p>
      </div>
    </div>
  );
}

const PLATFORM_HOSTS = new Set(["rpsignage.com", "www.rpsignage.com", "rpsignage.in", "www.rpsignage.in", "localhost", "127.0.0.1"]);

function isLocalNetworkHost(host) {
  return (
    /^192\.168\.\d{1,3}\.\d{1,3}$/.test(host) ||
    /^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host) ||
    /^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}$/.test(host)
  );
}

function isCustomBookingHost() {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname.toLowerCase();
  return host && !PLATFORM_HOSTS.has(host) && !isLocalNetworkHost(host);
}

function RootRoute() {
  if (isCustomBookingHost()) {
    return <CustomDomainEntry />;
  }
  return <HomeRoute />;
}

function HomeRoute() {
  const { user, loading } = useAuth();
  if (isNativePlayerShell()) {
    return <Navigate to="/play" replace />;
  }
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#070B16]">
        <div className="text-sm text-gray-400 font-mono uppercase tracking-widest animate-pulse">Loading...</div>
      </div>
    );
  }
  if (user && user !== false) {
    if (isNativeSocietyShell() && ["client", "society_staff", "society_member"].includes(user.role)) {
      if (user.role === "society_staff" && user.societyRole === "security") return <Navigate to="/society/guard" replace />;
      return <Navigate to="/society/mobile" replace />;
    }
    if (isNativeMarketingShell() && user.role === "admin") {
      return <Navigate to="/admin/marketing/whatsapp" replace />;
    }
    const dashboard = user.role === "admin" ? "/admin" : user.role === "dealer" ? "/dealer" : user.role === "auditor" ? "/society/audit" : user.role === "society_staff" ? (user.societyRole === "security" ? "/society/guard" : "/society/panel") : "/client";
    return <Navigate to={dashboard} replace />;
  }
  if (isNativeControlShell() || isNativeSocietyShell() || isNativeMarketingShell()) {
    return <Navigate to="/login" replace />;
  }
  return <EnterpriseHome />;
}

function ControllerUpdateBootstrap() {
  useEffect(() => {
    if (!isNativeControlShell()) return undefined;
    const startup = window.setTimeout(() => checkControllerAppUpdate(), 2000);
    const interval = window.setInterval(() => checkControllerAppUpdate({ force: true }), 6 * 60 * 60 * 1000);
    return () => {
      window.clearTimeout(startup);
      window.clearInterval(interval);
    };
  }, []);
  return null;
}

export default function App() {
  if (isNativePlayerShell()) {
    return (
      <AuthProvider>
        <BrowserRouter>
          <ControllerUpdateBootstrap />
          <SignagePlayer />
        </BrowserRouter>
      </AuthProvider>
    );
  }

  if (isCustomBookingHost()) {
    return (
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/*" element={<CustomDomainEntry />} />
          </Routes>
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    );
  }

  return (
    <AuthProvider>
      <BrowserRouter>
        <ControllerUpdateBootstrap />
        <WebsiteAnalyticsTracker />
        <Routes>
          <Route path="/" element={<RootRoute />} />
          <Route path="/login" element={<Login />} />
          <Route path="/switch-login" element={<SwitchLogin />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/products/:slug" element={<ProductDetailPage />} />
          <Route path="/e-catalogue" element={<ECataloguePage />} />
          <Route path="/e-catalogue/:slug" element={<ECatalogueDetailPage />} />
          <Route path="/solutions" element={<Navigate to="/industries" replace />} />
          <Route path="/solutions/:slug" element={<Navigate to="/industries" replace />} />
          <Route path="/industries" element={<IndustriesPage />} />
          <Route path="/industries/:slug" element={<IndustryDetailPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/request-demo" element={<RequestDemoPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogDetailPage />} />
          <Route path="/resources" element={<SimpleWebsitePage pageKey="resources" title="Resources" subtitle="Guides, product information and business display resources." />} />
          <Route path="/downloads" element={<DownloadsPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/privacy-policy" element={<SimpleWebsitePage pageKey="privacy-policy" title="Privacy Policy" subtitle="How RP Infotech handles information for the RP Society app, RP Signage website and related services." />} />
          <Route path="/terms-and-conditions" element={<SimpleWebsitePage pageKey="terms-and-conditions" title="Terms and Conditions" subtitle="Terms for using RP Signage software, services and related products." />} />
          <Route path="/refund-policy" element={<SimpleWebsitePage pageKey="refund-policy" title="Refund Policy" subtitle="Refund and cancellation guidelines for RP Infotech products and services." />} />
          <Route path="/cookie-policy" element={<SimpleWebsitePage pageKey="cookie-policy" title="Cookie Policy" subtitle="How RP Signage websites use cookies and similar technologies." />} />
          <Route path="/dealer-program" element={<DealerProgramPage />} />
          <Route path="/case-studies" element={<SimpleWebsitePage pageKey="case-studies" title="Case Studies" subtitle="Real deployments across restaurants, salons, schools, societies and businesses." />} />
          <Route path="/book/:clientId" element={<PublicBooking />} />
          <Route path="/agency/:clientSlug" element={<PublicAdAgency />} />
          <Route path="/society/approve/:visitorId" element={<PublicSocietyApproval />} />
          <Route path="/society/pay/:billId" element={<PublicSocietyBillPayment />} />
          <Route path="/society/resident-call/:callId" element={<SocietyWebCallDisabled />} />
          <Route path="/store/:clientRef/*" element={<PublicStore />} />
          <Route path="/shop/:clientRef/*" element={<PublicStore />} />
          <Route path="/retail/:clientRef/*" element={<PublicStore />} />
          <Route path="/hotel/:clientRef/*" element={<PublicStore />} />
          <Route path="/restaurant/:clientRef/*" element={<PublicStore />} />
          <Route path="/dine/:clientRef" element={<DiningSessionPage />} />
          <Route path="/menu/:clientRef" element={<PublicRestaurantMenu />} />
          <Route path="/hotel-staff" element={<HotelStaffApp />} />
          <Route path="/hotel-staff/:clientRef" element={<HotelStaffApp />} />
          <Route path="/hotel-login" element={<HotelStaffApp />} />
          <Route path="/hotel-login/:clientRef" element={<HotelStaffApp />} />
          <Route path="/kitchen" element={<HotelStaffApp forcedMode="kitchen" />} />
          <Route path="/kitchen/:clientRef" element={<HotelStaffApp forcedMode="kitchen" />} />
          <Route path="/preview/:pairCode" element={isNativeControlShell() || isNativeSocietyShell() ? <Navigate to="/login" replace /> : <SignagePlayer previewMode />} />
          <Route path="/play/:pairCode" element={isNativeControlShell() || isNativeSocietyShell() ? <Navigate to="/login" replace /> : <SignagePlayer />} />
          <Route path="/play" element={isNativeControlShell() || isNativeSocietyShell() ? <Navigate to="/login" replace /> : <SignagePlayer />} />

          <Route element={<ProtectedRoute role="admin"><Layout /></ProtectedRoute>}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/dealers" element={<AdminDealers />} />
            <Route path="/admin/templates" element={<AdminTemplates />} />
            <Route path="/admin/plans" element={<AdminPlans />} />
            <Route path="/admin/devices" element={<AdminDevices />} />
            <Route path="/admin/app-updates" element={<AdminAppUpdates />} />
            <Route path="/admin/payments" element={<AdminPayments />} />
            <Route path="/admin/billing/salon-otp" element={<AdminSalonOtpBilling />} />
            <Route path="/admin/clients" element={<AdminClients />} />
            <Route path="/admin/website" element={<AdminWebsite />} />
            <Route path="/admin/website-enquiries" element={<AdminWebsiteEnquiries />} />
            <Route path="/admin/website-analytics" element={<AdminWebsiteAnalytics />} />
            <Route path="/admin/seo" element={<AdminSeo />} />
            <Route path="/admin/e-catalogue" element={<AdminECatalogue />} />
            <Route path="/admin/marketing" element={<AdminMarketingCRM />} />
            <Route path="/admin/marketing/:tab" element={<AdminMarketingCRM />} />
          </Route>

          <Route element={<ProtectedRoute role="dealer"><Layout /></ProtectedRoute>}>
            <Route path="/dealer" element={<DealerDashboard />} />
            <Route path="/dealer/clients" element={<DealerClients />} />
            <Route path="/dealer/plans" element={<DealerPlans />} />
            <Route path="/dealer/screens" element={<DealerScreens />} />
            <Route path="/dealer/templates" element={<DealerTemplates />} />
            <Route path="/dealer/payments" element={<DealerPayments />} />
          </Route>

          <Route element={<ProtectedRoute role="client"><Layout /></ProtectedRoute>}>
            <Route path="/client" element={<ClientDashboard />} />
            <Route path="/client/devices" element={<ClientDevices />} />
            <Route path="/client/media" element={<ClientMedia />} />
            <Route path="/client/playlists" element={<ClientPlaylists />} />
            <Route path="/client/schedules" element={<ClientSchedules />} />
            <Route path="/client/templates" element={<ClientTemplates />} />
            <Route path="/client/payments" element={<ClientPayments />} />
            <Route path="/client/storefront" element={<ClientStorefront />} />
            <Route path="/client/society" element={<SocietyClientRoute><HousingSocietyPanel /></SocietyClientRoute>} />
            <Route path="/client/society/accounting/*" element={<SocietyClientRoute><AccountingLayout /></SocietyClientRoute>} />
            <Route path="/client/salon/accounting/*" element={<SalonAccounting />} />
          </Route>
          <Route path="/society/guard" element={<ProtectedRoute role={["client", "society_staff"]}><SocietyClientRoute><SocietyGuardApp /></SocietyClientRoute></ProtectedRoute>} />
          <Route path="/society/guard-call/:callId" element={<ProtectedRoute role={["client", "society_staff"]}><SocietyClientRoute><SocietyWebCallDisabled /></SocietyClientRoute></ProtectedRoute>} />
          <Route path="/society/resident-call/:callId" element={<ProtectedRoute role="society_member"><SocietyClientRoute><SocietyWebCallDisabled /></SocietyClientRoute></ProtectedRoute>} />
          <Route path="/society/audit" element={<ProtectedRoute role="auditor"><SocietyAuditorDashboard /></ProtectedRoute>} />
          <Route path="/society/panel" element={<ProtectedRoute role="society_staff"><SocietyStaffPanelRoute /></ProtectedRoute>} />
          <Route path="/society/mobile" element={<ProtectedRoute role={["client", "society_staff", "society_member"]}><SocietyClientRoute><SocietyMobileApp /></SocietyClientRoute></ProtectedRoute>} />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}
